import React, { useState, useRef } from 'react';
import { connect } from 'react-redux';
import { bool, object, oneOf } from 'prop-types';
import classNames from 'classnames';
import { isNil } from 'lodash';
import { getContext, I18n } from '@fiverr-private/fiverr_context';
import { Avatar, Button } from '@fiverr-private/fit';
import {
    reportContactSellerClick,
    reportContactSellerOptionsClick,
    onGuestClick,
    redirectToInboxConversation
} from '../../../utils/contactSeller';
import { shouldRenderCustomOrder } from '../../../common/customOrder';
import { VARIANTS } from '../../../types/variants';
import { AVATAR_VARIANTS, CHAT_BUTTON_VARIANTS, TRANSLATION_KEY } from './constants';

import './style.scss';

const ContactSeller = ({
    isOnline = false,
    variant = VARIANTS.B,
    overview = {},
    customOrder = {},
    shrinkChat = false
}, {
    general,
    pathfinderWrapper,
    biEvents,
    logger
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const modalRef = useRef();

    const { userId } = getContext();
    const { seller } = overview;
    const { username, profilePhoto } = seller;

    const customOrderEligible = shouldRenderCustomOrder({
        userId,
        customOrder,
        general
    });

    const reportContactSeller = () => reportContactSellerClick({
        biEvents,
        mixpanelSourceComponent: 'sticky_cta',
        bqSourceName: 'sticky_cta',
        metricKey: 'mobile_sticky_cta.contact_seller.click'
    });

    const lazyLoadModal = async() => {
        try {
            modalRef.current = await import(/* webpackChunkName: 'ContactSellerModal' */'../ContactSellerModal');
        } catch (error) {
            logger.error(error, { description: 'Failed to lazy load ContactSellerModal modal.' });
        }
    };

    const close = () => setIsModalOpen(false);

    const open = async() => {
        try {
            reportContactSellerOptionsClick(biEvents, 'sticky_cta');
            await lazyLoadModal();
            setIsModalOpen(true);
        } catch (error) {
            logger.error(error, { description: 'Failed to lazy load ContactSellerModal modal.' });
            redirectUserOrGuest();
        }
    };

    const redirect = () => {
        reportContactSeller();
        redirectUserOrGuest();
    };

    const redirectUserOrGuest = () => {
        const { userId } = getContext();

        if (!userId) {
            onGuestClick({ biEvents, pathfinderWrapper });
        } else {
            redirectToInboxConversation(pathfinderWrapper);
        }
    };

    const onClick = customOrderEligible ? open : redirect;

    const modalHasBeenLoaded = !isNil(modalRef.current);
    const Modal = modalHasBeenLoaded ? modalRef.current.default : () => null;

    return (
        <>
            {isModalOpen && customOrderEligible &&
                <Modal isOpen={isModalOpen}
                    onClose={close}
                    onQuestionClick={redirect}/>
            }
            <Button {...CHAT_BUTTON_VARIANTS[variant]}
                className={classNames(CHAT_BUTTON_VARIANTS[variant].className, { 'shrink-chat': shrinkChat })}
                onClick={onClick}>
                <Avatar {...AVATAR_VARIANTS[variant]}
                    username={username}
                    src={profilePhoto}
                    showOnlineIndicator={isOnline}
                    isOnline={isOnline}/>
                <span className="m-l-8">
                    <I18n k={TRANSLATION_KEY}/>
                </span>
            </Button>
        </>
    );
};

ContactSeller.propTypes = {
    variant: oneOf(Object.values(VARIANTS)),
    shrinkChat: bool,
    isOnline: bool,
    overview: object,
    customOrder: object
};

ContactSeller.contextTypes = {
    biEvents: object,
    general: object,
    pathfinderWrapper: object,
    logger: object
};

const mapStateToProps = ({ seller: { isOnline } = {} }) => ({
    isOnline
});

export { ContactSeller };
export default connect(mapStateToProps)(ContactSeller);
